import { ErrorMessage, Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Api, pathJoin } from '../../../services/api';
import { useDevMode } from '../../../services/devMode';
import { camelCaseToTitleCase } from '../../../services/formatting';
import { Notify } from '../../../services/notification/notify';
import { useFeatureContext } from '../../feature/featureContext';
import { Card } from '../cards/card';
import { HeadingCard } from '../cards/headingCard';
import { Button } from '../inputs/button';
import { MultiField } from '../inputs/multiInput';
import { Switch } from '../inputs/switch';
import { Load } from '../load/load';
import { LoadText } from '../load/loadText';
import { Changelog } from './changelog';

import styles from './form.module.css';
import { css } from '../../../services/css';

export const FormView = ({
    ids = [],
    resourceName = '',
    nameFunction = (d) => '',
    processData = (d) => d,
    format = {},
    restEndpoint = '',
    listLocation = '',
    headElement = <></>,
    formMode = FormMode.Edit,
    actionText = '',
}) => {

    const navigate = useNavigate();

    const [loading, setLoading] = useState(formMode === FormMode.Edit);
    const [data, setData] = useState([]);
    const isDev = useDevMode();
    const { setFeatureContext } = useFeatureContext();

    useEffect(() => {
        if (formMode === FormMode.Edit)
            getData();
    }, []);

    const getData = () => {
        if (ids) {
            setLoading(true);

            setFeatureContext({
                id: ids.join(','),
                loading: true
            });

            Api.get(pathJoin(restEndpoint, ids.join(',')))
            .then(json => {
                setData(json.content);
                setLoading(false);

                setFeatureContext({
                    id: ids.join(','),
                    loading: false,
                    name: (json.content.length > 1) ? 'Multiple ' + resourceName : nameFunction(json.content[0]),
                    navigable: json.content.length === 1
                });
            })
            .catch(e => {
                console.error(e);
                Notify.error('Error getting ' + resourceName.toLowerCase(), 'An error occurred while getting the data');
                setLoading(false);
                setFeatureContext({
                    id: ids.join(','),
                    loading: false
                });
            });
        } else {
            setLoading(false);
        }
    }

    const save = (values, { setSubmitting }) => {
        setLoading(true);

        (formMode === FormMode.Edit ? Api.patch : formMode === FormMode.Add ? Api.post : async () => {})(
            pathJoin(restEndpoint, ids.join(',')),
            formMode === FormMode.Add ? [processData(values)] : processData(values)
        )
        .then(json => {
            if (!json.success)
                throw new Error(json.message);

            setLoading(false);
            setSubmitting(false);

            if (formMode === FormMode.Add) {
                navigate(pathJoin(listLocation, json.content[0].id));
            } else if (formMode === FormMode.Edit) {
                goBack();
            }
        })
        .catch(e => {
            console.error(e);
            Notify.error('Error saving', 'An error occurred while saving the data.\n\n' + e.message);
            setLoading(false);
            setSubmitting(false);
        });
    }

    const goBack = () => {
        navigate(-1);
    }

    const len = () => ids?.length ?? 0;

    return (
        <div className={styles.View}>
            <header>
                <div className='title card shadow banner'>
                    {formMode === FormMode.Edit ?
                        <HeadingCard subtitle={<>
                            Edit&nbsp;
                            {loading ?  <LoadText dark={false} width='20px' /> : len()}&nbsp;
                            {resourceName}
                        </>}>
                            {loading ? <LoadText dark={false} max={50} strength={2} /> : data?.map(d => nameFunction(d))?.join(', ')}
                        </HeadingCard>
                    : formMode === FormMode.Add ?
                        <HeadingCard subtitle={<>Adding a new {resourceName}</>}>
                            New {resourceName}
                        </HeadingCard>
                    : ''}
                </div>
            </header>
                {
                    loading ?
                    <div className={styles.LoadWrapper}>
                        <div className={styles.Load}>
                            <Load message={'Getting ' + resourceName} />
                        </div>
                    </div>
                    :
                    <>
                        <Formik
                            initialValues={
                                Object.entries(format).reduce((init, [key, details]) => {
                                    init[key] = (formMode === FormMode.Add) ? details.default
                                        : len() > 1 ? null
                                        : len() === 1 ? details.fromApi?.(data?.[0]) ?? data?.[0]?.[key]
                                        : '';

                                    return init;
                                }, {})
                            }

                            validate={v => Object.entries(format).reduce((init, [key, details]) => {

                                init[key] = 
                                    (len() <= 1 && (details.required ?? true) && (v[key] === null || v[key] === '')) ? `Required`
                                    : (details.max !== null && details.max < v[key]?.length) ? `Too long. ${details.max} is the maximum length.`
                                    : (details.min !== null && details.min > v[key]?.length) ? `Too short. ${details.max} is the minimum length.`
                                    : (details.validate) ? (details.validate(v) ?? undefined)
                                    : undefined;

                                if (init[key] === undefined)
                                    delete init[key];

                                return init;
                            }, {})}

                            onSubmit={save}
                        >
                            {({values, setFieldValue, isSubmitting}) => (

                                <Form className={'formik ' + styles.Form}>
                                    {headElement}

                                    {
                                        isDev && <Card>
                                            <h1>Data</h1>
                                            <code>{JSON.stringify(values, null, '\t')}</code>
                                        </Card>
                                    }

                                    <Card>
                                        <h1>{resourceName} details</h1>

                                        {
                                            Object.entries(format).map(([key, details]) => {
                                                    const {
                                                        label,
                                                        type,
                                                        element,
                                                        rows,
                                                        switchData,
                                                        fromApi, forChangeLog, validate,
                                                        ...extraProps
                                                    } = details;

                                                    return <React.Fragment key={key}>
                                                        <label htmlFor={key}>{label ?? camelCaseToTitleCase(key)}</label>
                                                        <ErrorMessage name={key} component='div' className='error' />
                                                        {
                                                            type === 'custom' ?

                                                            element({
                                                                values,
                                                                setFieldValue,
                                                                isSubmitting,
                                                                key,
                                                                details,
                                                                data,
                                                            })

                                                            : type === 'textarea' ?
                                                            <MultiField
                                                                values={values}
                                                                setFieldValue={setFieldValue}
                                                                name={key}
                                                                as='textarea'
                                                                disabled={isSubmitting}
                                                                rows={rows ?? 3}
                                                                placeholder={label ?? camelCaseToTitleCase(key)}
                                                                dashOnEmpty={len() > 1}
                                                                emptyIsNull={len() > 1}
                                                                {...extraProps}
                                                            />
                                                            : type === 'switch' ?
                                                            <Switch
                                                                onChange={(e) => setFieldValue(key, e)}
                                                                initialValue={values[key]}
                                                                data={switchData}
                                                                {...extraProps}
                                                            />
                                                            : <MultiField
                                                                values={values}
                                                                setFieldValue={setFieldValue}
                                                                type={type ?? 'text'}
                                                                disabled={isSubmitting}
                                                                name={key}
                                                                placeholder={label ?? camelCaseToTitleCase(key)}
                                                                dashOnEmpty={len() > 1}
                                                                emptyIsNull={len() > 1}
                                                                {...extraProps}
                                                            />
                                                        }
                                                    </React.Fragment>
                                                })
                                        }

                                    </Card>

                                    {
                                        formMode === FormMode.Edit && <Card><Changelog
                                            data={data}
                                            nameFunction={nameFunction}
                                            format={format}
                                            values={values}
                                        /></Card>
                                    }

                                    <div className={styles.Actions}>
                                        <Button
                                            type='button'
                                            disabled={isSubmitting}
                                            onClick={() => goBack()}
                                            className={css(styles.ActionBtn, 'shadow')}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type='submit'
                                            disabled={isSubmitting}
                                            className={css(styles.ActionBtn, 'primary', 'shadow')}
                                        >
                                            { actionText ?  actionText : (formMode === FormMode.Edit ? 'Save'
                                                : formMode === FormMode.Add ? 'Add'
                                                : '') }
                                        </Button>
                                    </div>


                                </Form>
                            )}
                        </Formik>
                    </>
                }
        </div>
    )
}

/**
 * @typedef {number} FormModeEnum
 * @enum {FormModeEnum}
 */
export const FormMode = {
    Add: 1,
    Edit: 2
}